import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import './GmailConnectModal.css';
import { getIntegrations, connectSmtpAccount } from '../../store/Integration';

const INIT_FORM = { connectionType: 'smtp', firstName: '', lastName: '', email: '', appPassword: '', showPassword: false };

// isOpen        — controls visibility
// toggle        — called to close
// onDone        — called after any successful connection
// salesAccountId — optional, links the new integration to a SalesAccount
const GmailConnectModal = ({ isOpen, toggle, onDone, salesAccountId = null }) => {
    const dispatch = useDispatch();
    const { userDetails } = useSelector(state => state['Settings']) || {};

    const [result, setResult] = useState(null);
    const [form, setForm] = useState(INIT_FORM);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState('choose'); // 'choose' | 'form' | 'result'

    useEffect(() => {
        if (isOpen) {
            setView('choose');
            setForm({ ...INIT_FORM, firstName: userDetails?.['firstName'] || '', lastName: userDetails?.['lastName'] || '' });
            setLoading(false);
            setResult(null);
        }
    }, [isOpen, userDetails]);

    const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

    const handleClose = () => toggle();

    const handleMethodSelect = (connectionType) => {
        setField('connectionType', connectionType);
        setView('form');
    };

    const handleConnect = () => {
        setLoading(true);
        dispatch(connectSmtpAccount(
            { email: form['email'].trim(), appPassword: form['appPassword'].trim(), connectionType: form['connectionType'], firstName: form['firstName'].trim(), lastName: form['lastName'].trim(), ...(salesAccountId && { salesAccountId }) },
            () => {
                setLoading(false);
                if (onDone) onDone();
                handleClose();
            },
            (errMsg) => { setLoading(false); setResult({ success: false, errorMsg: errMsg }); setView('result'); }
        ));
    };

    const titles = { choose: 'Connect Your Google Account', form: 'Enter Your Credentials', result: result?.['success'] ? 'Account Connected' : 'Connection Failed' };

    const formInstructions = form['connectionType'] === 'smtp' ? {
        header: 'How to get your App Password',
        steps: [
            <span>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="gcm-link">Security Settings <i className="fa fa-external-link"></i></a> in your Google Account</span>,
            <span>Enable <a href="https://myaccount.google.com/signinoptions/two-step-verification" target="_blank" rel="noreferrer" className="gcm-link">2-Step Verification <i className="fa fa-external-link"></i></a> (required for App Passwords)</span>,
            <span>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="gcm-link">App Passwords <i className="fa fa-external-link"></i></a> → select <em>Mail</em> + <em>Other (Custom name)</em></span>,
            <span>Copy the 16-character password and paste it below</span>
        ]
    } : {
        header: 'How to connect a Workspace account',
        steps: [
            <span>Workspace admin: sign in to <a href="https://admin.google.com" target="_blank" rel="noreferrer" className="gcm-link">admin.google.com <i className="fa fa-external-link"></i></a></span>,
            <span>Go to <strong>Apps → Google Workspace → Gmail → End User Access</strong> and enable <strong>IMAP access</strong> for the user</span>,
            <span>The user enables <a href="https://myaccount.google.com/signinoptions/two-step-verification" target="_blank" rel="noreferrer" className="gcm-link">2-Step Verification <i className="fa fa-external-link"></i></a> on their Google account</span>,
            <span>Generate an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="gcm-link">App Password <i className="fa fa-external-link"></i></a> → select <em>Mail</em> + <em>Other (Custom name)</em></span>,
            <span>Copy the 16-character password and paste it below</span>
        ]
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} className="main-modal new-user-modal modal-dialog-centered gmail-connect-modal">
            <ModalHeader toggle={handleClose}><h3 data-testid="gmail-modal-title">{titles[view]}</h3></ModalHeader>
            <ModalBody>
                <div className="modal-area" data-testid="gmail-modal-body">

                    {view === 'choose' && (
                        <>
                            <p className="gcm-subtitle">Choose how you want to connect your Gmail account to SellAgent.</p>
                            <div className="gcm-method-cards" data-testid="gmail-method-cards">
                                <div className="gcm-method-card" data-testid="gmail-method-smtp" onClick={() => handleMethodSelect('smtp')}>
                                    <div className="gcm-card-icon smtp"><i className="fa fa-key"></i></div>
                                    <div className="gcm-card-text">
                                        <div className="gcm-card-title-row">
                                            <span className="gcm-card-title">App Password</span>
                                            <span className="gcm-badge-recommended" data-testid="gmail-method-smtp-recommended">Recommended</span>
                                        </div>
                                        <div className="gcm-card-desc">Connect via SMTP using a Google App Password. Requires 2-factor authentication to be enabled.</div>
                                    </div>
                                    <i className="fa fa-chevron-right gcm-card-arrow"></i>
                                </div>

                                <div className="gcm-method-card" data-testid="gmail-method-workspace" onClick={() => handleMethodSelect('workspace')}>
                                    <div className="gcm-card-icon workspace"><i className="fa fa-building"></i></div>
                                    <div className="gcm-card-text">
                                        <div className="gcm-card-title-row">
                                            <span className="gcm-card-title">Google Workspace Admin</span>
                                        </div>
                                        <div className="gcm-card-desc">For domain admins. Approve SellAgent in Google Admin first to get higher sending limits.</div>
                                    </div>
                                    <i className="fa fa-chevron-right gcm-card-arrow"></i>
                                </div>
                            </div>
                        </>
                    )}

                    {view === 'form' && (
                        <>
                            <div className="gcm-method-banner">
                                <div className={`gcm-method-banner-icon ${form['connectionType'] === 'smtp' ? 'smtp' : 'workspace'}`}>
                                    <i className={`fa ${form['connectionType'] === 'smtp' ? 'fa-key' : 'fa-building'}`}></i>
                                </div>
                                <div className="gcm-method-banner-text">
                                    <div className="gcm-method-banner-title">{form['connectionType'] === 'smtp' ? 'App Password' : 'Google Workspace Admin'}</div>
                                    <div className="gcm-method-banner-sub">{form['connectionType'] === 'smtp' ? 'SMTP via Gmail App Password' : 'SMTP via Workspace approval'}</div>
                                </div>
                            </div>
                            <div className="gcm-instructions">
                                <div className="gcm-instructions-header">{formInstructions['header']}</div>
                                <ol>
                                    {formInstructions['steps'].map((step, i) => <li key={i}>{step}</li>)}
                                </ol>
                            </div>
                            <div className="gcm-name-row">
                                <div className="form-group mb-3">
                                    <label className="gcm-form-label">First Name</label>
                                    <input type="text" data-testid="gmail-input-firstname" className="form-control" placeholder="Jane" value={form['firstName']} onChange={e => setField('firstName', e['target']['value'])} disabled={loading} />
                                </div>
                                <div className="form-group mb-3">
                                    <label className="gcm-form-label">Last Name</label>
                                    <input type="text" data-testid="gmail-input-lastname" className="form-control" placeholder="Doe" value={form['lastName']} onChange={e => setField('lastName', e['target']['value'])} disabled={loading} />
                                </div>
                            </div>
                            <div className="form-group mb-3">
                                <label className="gcm-form-label">Gmail Address</label>
                                <div className="gcm-input-wrapper">
                                    <input type="email" data-testid="gmail-input-email" className="form-control gcm-input-with-left-icon" placeholder={form['connectionType'] === 'smtp' ? 'you@gmail.com' : 'you@yourdomain.com'} value={form['email']} onChange={e => setField('email', e['target']['value'])} disabled={loading} />
                                </div>
                            </div>
                            <div className="form-group mb-3">
                                <label className="gcm-form-label">App Password</label>
                                <div className="gcm-input-wrapper">
                                    <input type={form['showPassword'] ? 'text' : 'password'} data-testid="gmail-input-apppassword" className="form-control gcm-input-with-right-icon" placeholder="xxxx xxxx xxxx xxxx" value={form['appPassword']} onChange={e => setField('appPassword', e['target']['value'])} disabled={loading} />
                                    <i data-testid="gmail-toggle-password-visibility" className={`fa ${form['showPassword'] ? 'fa-eye-slash' : 'fa-eye'} gcm-input-icon-right`} onClick={() => setField('showPassword', !form['showPassword'])} />
                                </div>
                                <div className="gcm-input-hint">16 characters — spaces are optional</div>
                            </div>
                        </>
                    )}

                    {view === 'result' && result?.['success'] && (
                        <div className="gcm-result">
                            <div className="gcm-result-circle success"><i className="fa fa-check"></i></div>
                            <div className="gcm-result-title">Account connected</div>
                            <div className="gcm-result-email">{result['email']}</div>
                            {!result['imapEnabled'] && (
                                <div className="gcm-imap-warning">
                                    <i className="fa fa-exclamation-triangle"></i>
                                    Reply tracking is limited — IMAP could not connect, but sending works normally.
                                </div>
                            )}
                        </div>
                    )}

                    {view === 'result' && !result?.['success'] && (
                        <div className="gcm-result">
                            <div className="gcm-result-circle error"><i className="fa fa-times"></i></div>
                            <div className="gcm-result-title">Connection failed</div>
                            <div className="gcm-error-msg">{result?.['errorMsg'] || 'An unexpected error occurred. Please try again.'}</div>
                        </div>
                    )}

                    <div className="group-btns mt-4 d-flex justify-content-end">
                        {view === 'choose' && <button data-testid="gmail-choose-cancel" className="btn-style-border" onClick={handleClose}>Cancel</button>}
                        {view === 'form' && (
                            <>
                                <button data-testid="gmail-form-back" className="btn-style-border mr-2" onClick={() => setView('choose')} disabled={loading}>Back</button>
                                <button data-testid="gmail-form-submit" className="btn-style-new" onClick={handleConnect} disabled={loading || !form['firstName'].trim() || !form['email'].trim() || !form['appPassword'].trim()}>
                                    {loading ? <><i className="fa fa-spinner fa-spin mr-1"></i> Verifying...</> : 'Connect & Verify'}
                                </button>
                            </>
                        )}
                        {view === 'result' && (
                            <>
                                {!result?.['success'] && <button data-testid="gmail-result-retry" className="btn-style-border mr-2" onClick={() => setView('form')}>Try Again</button>}
                                <button data-testid="gmail-result-close" className="btn-style-new" onClick={handleClose}>{result?.['success'] ? 'Done' : 'Close'}</button>
                            </>
                        )}
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default GmailConnectModal;
